import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { COMMA, ENTER } from '@angular/cdk/keycodes'
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core'
import { UntypedFormControl } from '@angular/forms'
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete'
import { MatFormFieldControl } from '@angular/material/form-field'
import { BaseModel } from '@mmx/shared'
import Fuse from 'fuse.js'
import {
  concat,
  filter,
  find,
  first,
  isArray,
  isEmpty,
  isEqual,
  isNil,
  isString,
  uniq,
  uniqWith,
  without,
} from 'lodash'
import { Observable } from 'rxjs'
import { map, startWith } from 'rxjs/operators'

import { CustomMaterialInputControlComponent } from '../custom-field'

@Component({
  selector: 'mmx-chips-selector-input',
  templateUrl: './chips-selector.component.html',
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: ChipsSelectorInputComponent,
    },
  ],
  standalone: false,
})
export class ChipsSelectorInputComponent<Type extends BaseModel>
  extends CustomMaterialInputControlComponent<Type[]>
  implements OnInit
{
  @ViewChild('selectCptInput') selectCptInput: ElementRef
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger

  public separatorKeysCodes = [ENTER, COMMA]
  productsCtrl: UntypedFormControl = new UntypedFormControl()
  filteredProducts: Observable<Type[]>
  protected availableItems: Type[] = []

  protected fuse: Fuse<Type>

  get value() {
    return this._value ? this._value : []
  }
  set value(products: any) {
    delete this.selectedItemIds

    if (isString(first(products))) {
      if (this.availableItems.length > 0) {
        products = filter(
          uniq(products).map((productId) => {
            return find(this.availableItems, (product) => {
              return this.getItemId(product) === productId
            })
          }),
        ) as Type[]
      } else {
        this.selectedItemIds = products
        return
      }
    }

    if (isArray(products) && products.length >= 0) {
      this._value = filter(products)
      this.stateChanges.next()

      if (this.onChange) {
        this.onChange(this.value)
      }
    }

    if (products === null) {
      this._value = null
    }
  }

  @Input()
  get selectAllOption() {
    return this._selectAll
  }
  set selectAllOption(req) {
    this._selectAll = coerceBooleanProperty(req)
    this.stateChanges.next()
  }
  protected _selectAll = false

  protected selectedItemIds: string[]

  ngOnInit() {
    super.ngOnInit()

    // listen for input change and update autocomplete accordingly
    this.filteredProducts = this.productsCtrl.valueChanges.pipe(
      startWith(''),
      map((input: Type | string) => {
        if (
          typeof input === 'string' &&
          input.includes(',') &&
          input.includes('-')
        ) {
          // allow pasting of several products at once
          const productIds: string[] = []
          for (const product of this.value) {
            productIds.push(
              typeof product === 'string' ? product : this.getItemId(product),
            )
          }
          productIds.push(...input.split(','))
          this.value = productIds
          input = ''
          this.productsCtrl.setValue('')
        }

        return input
          ? this.filterAutocomplete(input)
          : without(this.availableItems, ...this.value)
      }),
    )
  }

  filterAutocomplete(input: Type | string) {
    const productsWithoutSelected = without(this.availableItems, ...this.value)

    if (isEmpty(input) || input instanceof BaseModel || !this.fuse) {
      return productsWithoutSelected
    } else {
      this.fuse.setCollection(productsWithoutSelected)
      return this.fuse.search(input).map((result) => result.item)
    }
  }

  copySelectedValue() {
    const text = (this.value || []).map((item) =>
      typeof item === 'string' ? item : this.getItemId(item),
    )
    const selBox = document.createElement('textarea')
    selBox.style.position = 'fixed'
    selBox.style.left = '0'
    selBox.style.top = '0'
    selBox.style.opacity = '0'
    selBox.value = text.join(',')
    document.body.appendChild(selBox)
    selBox.focus()
    selBox.select()
    document.execCommand('copy')
    document.body.removeChild(selBox)
  }

  removeSelectedCode(index?: number) {
    let componentValue = this.value || []

    if (typeof index === 'number' && index >= 0) {
      componentValue.splice(index, 1)
    } else if (isNil(index)) {
      componentValue = []
    }

    this.value = componentValue
    this.productsCtrl.setValue(null)

    this.focus()
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const product = event.option.value
    const componentValue = this.value || []

    // Ensure that no input data isn't added twice to the selected products list
    if (componentValue.indexOf(product) === -1) {
      componentValue.push(product)
      this.value = componentValue

      // clear the partial search value
      this.productsCtrl.setValue(null)
    }

    // this.selectCptInput.nativeElement.value = ''
    this.productsCtrl.setValue(null)

    this.onItemSelected(product)
  }

  // Select all the available options
  selectAll() {
    const products = this.productsCtrl.value
      ? this.filterAutocomplete(this.productsCtrl.value)
      : this.availableItems
    this.values = products
  }

  focus() {
    this.selectCptInput.nativeElement.focus()
  }

  set values(products: Type[]) {
    const values = uniqWith(concat(this._value, products), isEqual)
    this.value = values
    this.productsCtrl.setValue(null)
    this.autocomplete.closePanel()
  }

  getItemLabel(item: Type) {
    const i: any = item
    return i.viewValue || i.name || i.label
  }

  getItemId(item: Type) {
    return item.id
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onItemSelected(item: Type): void {}

  protected setAvailableItems(items: Type[], keys: string[] = ['viewValue']) {
    this.availableItems = items

    this.fuse = new Fuse(this.availableItems, {
      keys,
      shouldSort: true,
    })

    if (this.selectedItemIds != null) {
      // this triggers the set value handler, which converts the value to be an array of products
      this.value = this.selectedItemIds
    }

    this.productsCtrl.updateValueAndValidity({
      emitEvent: true,
      onlySelf: true,
    })
  }
}
